import React, { useCallback, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  BiData,
  ViewOffersAdoptablesClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ActionBarItemWrapper from '../../_partials/items/ActionBarItemWrapper';
import { pushAdoptsSellerModal } from '../../../Modals/redux/actionCreators';
import {
  getIsAdoptableFixedPriceType,
  getIsAdoptableOfferType,
  getPrice,
} from '../../../utils/adoptables';
import { StandardDeviationElementPlacementContext } from '../../ElementPlacementContext/variants/standard/context';
import { useEndSaleAction } from '../../../Sidebar/SidebarBlock/layouts/exclusives/hooks';
import type { AdoptableSellerElementGroup } from '../../ElementPlacementContext/variants/standard/types';

import s from './AdoptableSeller.scss';

export interface Props {
  deviation: PapiDeviation;
  /**
   * A stop gap to allow our stash actionbar to pass an element group without relying on the
   * {@link StandardDeviationElementPlacementContext}.
   *
   * Passing this will override any context values, present or not.
   */
  // TODO: Figure out how to provide alternate element placement contexts based on bar type (stash, regular, simple, etc)
  manualElementGroup?:
    | Omit<AdoptableSellerElementGroup, 'groupPlacement' | 'countOnBar'>
    | false;
  className?: string;
}

export const AdoptableSeller: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  manualElementGroup,
  className,
}) => {
  const {
    elementGroups: { adoptableSeller: contextElementGroup },
  } = useContext(StandardDeviationElementPlacementContext);
  const elementGroup = manualElementGroup ?? contextElementGroup;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const [endSaleAction, endSaleBiData] = useEndSaleAction(deviation, {
    sectionname: 'action_bar',
  });

  const openOfferModal = useCallback(() => {
    dispatch(pushAdoptsSellerModal([deviation], 'offers'));
  }, [deviation, dispatch]);

  if (isMobile || !elementGroup) {
    return null;
  }

  const isOfferType = getIsAdoptableOfferType(deviation);
  const isFixedPriceType = getIsAdoptableFixedPriceType(deviation);
  const price = getPrice(deviation);

  return (
    <ActionBarItemWrapper
      className={className}
      popoverWrapperClassName={s['wrapper']}
    >
      <div className={s['status']}>
        {isOfferType && !isFixedPriceType && (
          <span>{t('actionbar.adoptables.openToOffers')}</span>
        )}
        {!isOfferType && isFixedPriceType && (
          <span>
            <Trans i18nKey={'actionbar.adoptables.adoptForDollars'}>
              Adopt for ${{ price }}
            </Trans>
          </span>
        )}
        {isOfferType && isFixedPriceType && (
          <div>
            <Trans i18nKey={'actionbar.adoptables.adoptForDollars'}>
              Adopt for ${{ price }}
            </Trans>
            <span className={s['subscript']}>
              {t('actionbar.adoptables.subtitle')}
            </span>
          </div>
        )}
      </div>
      <div className={s['buttons']}>
        {elementGroup.endSale && (
          <BuyButton
            variant={'outline'}
            size={'medium'}
            rounded={true}
            onClick={endSaleAction}
            className={s['button']}
            biData={endSaleBiData}
          >
            {t('actionbar.adoptables.endSale')}
          </BuyButton>
        )}
        {elementGroup.viewOffers && (
          <BuyButton
            variant={'outline'}
            size={'medium'}
            rounded={true}
            onClick={openOfferModal}
            className={s['button']}
            biData={BiData<ViewOffersAdoptablesClickBiEvent>({
              evid: 746,
              itemid: deviation.deviationId,
              typeid: deviation.typeId,
              sectionname: 'action_bar',
            })}
          >
            {t('actionbar.adoptables.viewOffers')}
          </BuyButton>
        )}
      </div>
    </ActionBarItemWrapper>
  );
};
AdoptableSeller.displayName = 'AdoptableSeller';

export default AdoptableSeller;
